import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  Stack,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { MoreVert as MoreVertIcon,
  AttachMoneyOutlined as AttachMoneyOutlinedIcon

} from '@mui/icons-material';
import { ReactComponent as PrintIcon } from '../../../../../assets/SVG/printerblue.svg';
import CustomPagination from '../../../../../components/common/CustomPagination';
import TableSkeleton from '../../../../../components/common/TableSkeleton';
import useCurrency from '../../../../../components/CurrencyProvider';
import style from '../../../Advance/Advance.module.css';
import { formatDateDMY } from '../../../../../utils/dateUtils';
import { useTranslation } from 'react-i18next';

interface AdvanceRefundLogRow {
	id: number;
	refundDate: string;
	billNo: string;
	receiptNo: string;
	refundAmount: number;
	refundPaidAmount: number;
	refundBalanceAmount: number;
	refundReason: string;
  bill_advance_number: string | null;

}

const refundLogsData: AdvanceRefundLogRow[] = [];

const InvoiceLogs: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const { t: tBilling } = useTranslation('billing_config');
  const [page, setPage] = useState(0);
  const [isLoading] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<AdvanceRefundLogRow | null>(null);
  const menuOpen = Boolean(menuAnchor);

  const [pageSize, setPageSize] = useState(10);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(0);
  }, []);

  const columns = useMemo(
    () => [
      tBilling('RefundDetails.logsColumns.refundDate', 'Refund Date'),
      tBilling('RefundDetails.logsColumns.billNo', 'Bill No.'),
      tBilling('Refund.receipt_no', 'Bill/Advance Number'),        
      tBilling('Refund.refund_receipt_no', 'Receipt No.'),
      tBilling('RefundDetails.logsColumns.refundAmount', 'Refund Amount'),
      tBilling('RefundDetails.logsColumns.refundPaidAmount', 'Refund Paid Amount'),
      tBilling('RefundDetails.logsColumns.refundBalanceAmount', 'Refund Balance Amount'),
      tBilling('Refund.refund_reason', 'Refund Reason'),
      tBilling('Advance.action', 'Action'),
    ],
    [tBilling]
  );

  const totalCount = refundLogsData.length;
  const startIndex = page * pageSize;
  const paginatedRows = refundLogsData.slice(startIndex, startIndex + pageSize);

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: AdvanceRefundLogRow) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedRow(null);
  };

  return (
    <Box sx={{ mt: 2 }} data-testid="invoice-logs-container-main" aria-label={tBilling('RefundDetails.aria.invoiceLogsContainer', 'Invoice refund logs container')}>
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 'none', elevation: 0 }} data-testid="invoice-logs-container-table">
        <Table aria-label={tBilling('RefundDetails.aria.invoiceLogsTable', 'Advance refund logs list')} data-testid="invoice-logs-table-list">
          <TableHead>
            <TableRow sx={{ bgcolor: '#FAFAFA' }}>
              {columns.map((col, index) => (
                <TableCell
                  key={col}
                  className={style.tableHeader}
                  sx={{ whiteSpace: 'nowrap' }}
                  align={index >= 3 && index <= 5 ? 'right' : index === columns.length - 1 ? 'right' : 'left'}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableSkeleton
                columnWidths={[120, 160, 260, 160, 180, 200, 260, 120]}
                rows={pageSize}
              />
            )}

            {!isLoading &&
							paginatedRows.map((row) => (
							  <TableRow
							    hover
							    key={row.id}
							    tabIndex={0}
							    aria-label={tBilling('RefundDetails.aria.invoiceLogRow', 'Invoice log row')}
							    data-testid={`invoice-logs-row-${row.id}`}
							  >
							    <TableCell className={style.tableCells}>{row.refundDate ? formatDateDMY(row.refundDate) : '-'}</TableCell>
							    <TableCell className={style.tableCells}>{row.billNo ?? '-'}</TableCell>
							    <TableCell className={style.tableCells}>{row.bill_advance_number ?? '-'}</TableCell>
							    <TableCell className={style.tableCells}>{row.receiptNo ?? '-'}</TableCell>
							    <TableCell className={style.tableCells} align="right">
							      {formatCurrency(row.refundAmount)}
							    </TableCell>
							    <TableCell className={style.tableCells} align="right">
							      {formatCurrency(row.refundPaidAmount)}
							    </TableCell>
							    <TableCell className={style.tableCells} align="right">
							      {formatCurrency(row.refundBalanceAmount)}
							    </TableCell>
							    <TableCell className={style.tableCells}>{row.refundReason ?? '-'}</TableCell>
							    <TableCell className={style.tableCells} align="right" sx={{ minWidth: 120 }}>
							      <Stack
							        direction="row"
							        spacing={1}
							        justifyContent="flex-end"
							        alignItems="center"
							      >
							        <IconButton
							          size="small"
							          aria-label={tBilling('RefundDetails.aria.printRefundLog', 'Print refund log')}
							          data-testid={`invoice-logs-button-print-${row.id}`}
							        >
							          <PrintIcon />
							        </IconButton>
							        {row.refundBalanceAmount > 0 ? (
							          <IconButton
							            size="small"
							            aria-label={tBilling('RefundDetails.aria.openRefundLogActions', 'Open refund log actions')}
							            onClick={(e) => handleMenuOpen(e, row)}
							            data-testid={`invoice-logs-button-actions-${row.id}`}
							          >
							            <MoreVertIcon />
							          </IconButton>
							        ) : (
							          <Box sx={{ width: 35, height: 24 }} />
							        )}
							      </Stack>
							    </TableCell>
							  </TableRow>
							))}

            {!isLoading && paginatedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography variant="body2">{tBilling('Refund.no_entries_found', 'No entries found.')}</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <CustomPagination
          page={page}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[10,15,20,25]}
          totalCount={totalCount}
          onPageChange={handlePageChange}
        />
      </TableContainer>

      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{ sx: { borderRadius: 2 } }}
        data-testid="invoice-logs-modal-actions"
        aria-label={tBilling('RefundDetails.aria.invoiceLogsActionsMenu', 'Invoice logs actions menu')}
      >
        <MenuItem onClick={handleMenuClose} disabled={!selectedRow}>
          <AttachMoneyOutlinedIcon fontSize="small" />
          {tBilling('RefundDetails.actions.payment', 'Payment')}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default InvoiceLogs;

